import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttributePill, ActionChip, SystemChip } from './pills';
import { ConfirmDialog } from './ConfirmDialog';
import { useWriteFeedback } from './useWriteFeedback';
import { DueDate } from './InvoiceRows';

export interface InvoiceDetail {
  id: string;
  number: string;
  amount: number;
  dueDate: string | null;
  status: string;
  sentAt: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  company: { id: string; name: string } | null;
  lead: { id: string; name: string | null } | null;
  deal: { id: string; name: string } | null;
  lineItems: Array<{ id: string; description: string; amount: number }>;
  lineSum: number;
  recentActivity: Array<{ id: string; type: string; summary: string; occurredAt: string }>;
}

// #50 (cmdr/DevOps, 2026-08-23 — applied from day one here, not retrofitted): on a real invoiceId
// CHANGE, reset synchronously to an honest empty shell, never null. `invoice` stays truthy the whole
// revalidating window (no detach), every field reads absent (no wrong-entity-flash). `status` empties
// to `''` (not a guessed real status) so the Send/Mark-paid/Void gates below never show a wrong action.
function emptyInvoice(id: string): InvoiceDetail {
  return { id, number: '', amount: 0, dueDate: null, status: '', sentAt: null, paidAt: null, voidedAt: null, company: null, lead: null, deal: null, lineItems: [], lineSum: 0, recentActivity: [] };
}

/**
 * InvoiceDetailPanel — #62 next-phase Invoices depth spec (UXeng/cmdr, 2026-08-23), first of the
 * remaining-6 build. ONE surface for create AND detail/edit, same "create and detail cannot drift
 * apart" reasoning as every other panel in this suite.
 *
 * Status is a read-only AttributePill (an attribute, no free picker) — transitions are ACTION-driven
 * only (Send/Mark paid/Void ActionChips), "must not look alike" (UXeng §2). Void is terminal,
 * `text-opt-rose`, separated from the other two.
 */
export const InvoiceDetailPanel: React.FC<{
  invoiceId: string | 'new';
  onClose: () => void;
  onCreated: (invoice: InvoiceDetail) => void;
  onUpdated: (invoice: InvoiceDetail) => void;
}> = ({ invoiceId, onClose, onCreated, onUpdated }) => {
  const { t } = useTranslation('common');
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>(invoiceId === 'new' ? 'ready' : 'loading');
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [createCompanyId, setCreateCompanyId] = useState<string | null>(null);
  // B3 / D-I6 — the payer is a company XOR a contact (AC-INV1): the create form carries the same
  // link-kind toggle as Deal/Ticket create. Plain selects per DN-8 (the searchable picker is B6's;
  // shipping without it is the ruled degradation). Switching kind clears the other side's pick so
  // the form can never SEND both.
  const [createLinkKind, setCreateLinkKind] = useState<'company' | 'contact'>('company');
  const [createContactId, setCreateContactId] = useState<string | null>(null);
  const [contacts, setContacts] = useState<{ id: string; name: string | null }[]>([]);
  const [createAmount, setCreateAmount] = useState('');
  const [createDue, setCreateDue] = useState('');
  const [editingField, setEditingField] = useState<'amount' | 'dueDate' | null>(null);
  const [fieldDraft, setFieldDraft] = useState('');
  const [lineDescDraft, setLineDescDraft] = useState('');
  const [lineAmountDraft, setLineAmountDraft] = useState('');
  const [isAddingLine, setIsAddingLine] = useState(false);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (invoiceId === 'new') return;
    // 🔴 root fix (cmdr/DevOps, 2026-08-25): right after create, the parent flips invoiceId from 'new'
    // to the real id on the SAME panel instance (no remount) — this effect re-fires, but `invoice` here
    // ALREADY holds fresh data for that exact id (submitCreate's own setInvoice ran in the same event).
    // Re-fetching anyway forced a real, if brief, `loadState:'loading'` window — and every inline-edit
    // click handler gates on `!isLoading`, so a click landing in that window was SILENTLY SWALLOWED (no
    // input opened, no error, nothing) — a genuine user-facing bug, not a test artifact. Skip the
    // redundant round-trip when we already have this exact invoice.
    if (invoice && invoice.id === invoiceId) { hasLoadedRef.current = true; return; }
    setLoadState('loading');
    if (hasLoadedRef.current) setInvoice(emptyInvoice(invoiceId));
    fetch(`/api/invoices/${invoiceId}`).then((r) => r.ok ? r.json() : Promise.reject(r.status)).then((d: InvoiceDetail) => {
      setInvoice(d); setLoadState('ready'); hasLoadedRef.current = true;
    }).catch(() => setLoadState('error'));
  }, [invoiceId]);

  useEffect(() => {
    fetch('/api/companies').then((r) => r.ok ? r.json() : []).then(setCompanies).catch(() => {});
    fetch('/api/customers?kind=contact').then((r) => r.ok ? r.json() : []).then((rows) => setContacts((rows as any[]).map((c) => ({ id: c.id, name: c.name })))).catch(() => {});
  }, []);

  // A3 (design §4.3) — write failures visible; landed-but-unconfirmed is a third outcome. On THIS
  // panel the separation is load-bearing for money: a failed-send toast that lied about a landed
  // send is what invites the double-send; the unconfirmed copy re-CHECKS, never re-submits.
  const feedback = useWriteFeedback();
  const [createError, setCreateError] = useState(false);
  const [pendingCreatedId, setPendingCreatedId] = useState<string | null>(null);
  const [lineError, setLineError] = useState(false);

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(createAmount);
    const payerId = createLinkKind === 'company' ? createCompanyId : createContactId;
    if (!payerId || !createAmount.trim() || Number.isNaN(amount)) return;
    const res = await fetch('/api/invoices', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      // Exactly one payer VALUE is ever non-null — the toggle IS the XOR at the form (AC-INV1's UI
      // half). Both keys stay literal (not a spread of a ternary) so field-reachability's
      // top-level-key scan can see this sender.
      body: JSON.stringify({ companyId: createLinkKind === 'company' ? payerId : null, leadId: createLinkKind === 'contact' ? payerId : null, amount, dueDate: createDue || null }),
    }).catch(() => null);
    // D-I7: a failed create is a SAVE error — form and typed amount stay; distinct message.
    if (!res?.ok) { setCreateError(true); return; }
    setCreateError(false);
    const { invoiceId: newId } = await res.json();
    const ok = await confirmCreated(newId);
    if (!ok) setPendingCreatedId(newId); // landed; re-check, never re-offer the form (a resubmit is a SECOND invoice)
  };

  const confirmCreated = async (newId: string): Promise<boolean> => {
    const detailRes = await fetch(`/api/invoices/${newId}`).catch(() => null);
    if (!detailRes?.ok) return false;
    const detail: InvoiceDetail = await detailRes.json();
    setPendingCreatedId(null);
    setInvoice(detail);
    onCreated(detail);
    return true;
  };

  const patch = async (data: Partial<{ amount: number; dueDate: string | null }>): Promise<boolean> => {
    if (!invoice) return false;
    const res = await fetch(`/api/invoices/${invoice.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).catch(() => null);
    if (!res?.ok) { feedback.fail(t('writeStates.saveFailed')); return false; }
    const updated: InvoiceDetail = await res.json();
    setInvoice(updated);
    onUpdated(updated);
    return true;
  };

  const refresh = async (): Promise<boolean> => {
    if (!invoice) return false;
    const r = await fetch(`/api/invoices/${invoice.id}`).catch(() => null);
    if (!r?.ok) return false;
    const refreshed: InvoiceDetail = await r.json();
    setInvoice(refreshed);
    onUpdated(refreshed);
    return true;
  };
  /** D-I5 — send/markPaid checked nothing: a failed write re-rendered the invoice as draft and the
   *  operator's retry-tap re-submits. (The original note said this "sends a customer two invoices";
   *  it does not — nothing is transmitted — but a double-submit still double-logs the activity and
   *  muddles sentAt, and the draft-gate is the backstop.) Three outcomes, separated; the
   *  unconfirmed one re-checks and never re-submits (the service's draft-gate is the backstop,
   *  not the UX). */
  const runVerb = async (write: () => Promise<Response>): Promise<boolean> => {
    const res = await write().catch(() => null);
    if (!res?.ok) { feedback.fail(t('writeStates.actionFailed')); return false; }
    if (await refresh()) { feedback.clear(); return true; }
    feedback.unconfirmed(t('writeStates.confirmFailed'), t('writeStates.recheck'), () => { void refresh().then((ok) => { if (ok) feedback.clear(); }); });
    return true;
  };

  // B2/D-I4, CORRECTED — this action does NOT leave the building. `InvoiceService.sendInvoice`
  // checks the draft gate, sets status/sentAt and logs an activity: no email, no PDF, no
  // transmission. The original spec (mine) reasoned "nothing un-sends an email" and specified
  // confirm-first from that; the premise was never verified and is false. Confirm-first still
  // holds, on the real irreversibility: marking sent FREEZES the composition (P16/D-I2) — "a
  // consequence the user cannot see is a consequence they did not agree to". Mark paid stays
  // one-tap. If real transmission is ever built it is a NEW verb, not this one growing a side
  // effect.
  const [confirmingSend, setConfirmingSend] = useState(false);
  const send = async () => {
    if (!invoice) return;
    setConfirmingSend(false);
    await runVerb(() => fetch(`/api/invoices/${invoice.id}/send`, { method: 'POST' }));
  };
  const markPaid = async () => { if (!invoice) return; await runVerb(() => fetch(`/api/invoices/${invoice.id}/mark-paid`, { method: 'POST' })); };
  // Void confirms via the ratified ConfirmDialog, never window.confirm (P7): on the one terminal
  // financial action here, the native dialog's browser-language buttons beside a t() message were
  // the two-languages-in-one-dialog class. Confirm-first stays — void is irreversible.
  const [confirmingVoid, setConfirmingVoid] = useState(false);
  const voidInvoice = async () => {
    if (!invoice) return;
    setConfirmingVoid(false);
    await runVerb(() => fetch(`/api/invoices/${invoice.id}/void`, { method: 'POST' }));
  };

  const addLineItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice) return;
    const amount = Number(lineAmountDraft);
    if (!lineDescDraft.trim() || Number.isNaN(amount)) return;
    const res = await fetch(`/api/invoices/${invoice.id}/line-items`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description: lineDescDraft.trim(), amount }),
    }).catch(() => null);
    // D-LI5: a silent add-failure invites retyping — and if the first request actually landed, the
    // invoice carries the line TWICE. The error stays attached to the form; the drafts stay put.
    if (!res?.ok) { setLineError(true); return; }
    setLineError(false); // a successful retry clears the stale error
    const updated: InvoiceDetail = await res.json();
    setInvoice(updated);
    setLineDescDraft(''); setLineAmountDraft(''); setIsAddingLine(false);
  };

  const removeLineItem = async (lineItemId: string) => {
    if (!invoice) return;
    const res = await fetch(`/api/invoices/${invoice.id}/line-items/${lineItemId}`, { method: 'DELETE' }).catch(() => null);
    if (!res?.ok) { feedback.fail(t('writeStates.saveFailed')); return; } // the row stays — nothing changed
    const updated: InvoiceDetail = await res.json();
    setInvoice(updated);
  };

  const startEdit = (field: 'amount' | 'dueDate', current: string) => { setFieldDraft(current); setEditingField(field); };
  const saveEdit = () => {
    if (!editingField || !invoice) return;
    const f = editingField; setEditingField(null);
    const reopenOnFail = (ok: boolean) => { if (!ok) setEditingField(f); }; // draft survives the close
    if (f === 'amount') {
      const v = Number(fieldDraft);
      if (Number.isNaN(v) || v === invoice.amount) return;
      void patch({ amount: v }).then(reopenOnFail);
    } else {
      const v = fieldDraft || null;
      if (v === invoice.dueDate) return;
      void patch({ dueDate: v }).then(reopenOnFail);
    }
  };

  const isLoading = loadState === 'loading';

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto bg-canvas" data-testid="invoice-detail-panel">
      <button data-testid="invoice-detail-back" className="lg:hidden self-start flex items-center gap-1 px-4 py-3 min-h-[44px] text-sm text-primary font-semibold"
        onClick={onClose}>&larr; {t('taskBoard.back')}</button>

      <div className="flex flex-col gap-4 p-4 pb-16 lg:pb-4">
        {invoiceId === 'new' && !invoice && !pendingCreatedId && (
          <form data-testid="invoice-create-form" onSubmit={submitCreate} className="rounded-xl border border-border-neutral bg-surface p-4 flex flex-col gap-3">
            {/* B3/D-I6 — link-kind toggle, same shape as Deal create; the two selects are mutually
                exclusive in the DOM so the form cannot express a two-payer invoice. */}
            <div className="flex gap-2">
              <button type="button" data-testid="invoice-link-kind-company" onClick={() => { setCreateLinkKind('company'); setCreateContactId(null); }}
                className={`h-9 px-3 rounded-lg text-xs font-semibold ${createLinkKind === 'company' ? 'bg-primary text-white' : 'bg-canvas text-text-muted'}`}>{t('accountsGrid.columns.name')}</button>
              <button type="button" data-testid="invoice-link-kind-contact" onClick={() => { setCreateLinkKind('contact'); setCreateCompanyId(null); }}
                className={`h-9 px-3 rounded-lg text-xs font-semibold ${createLinkKind === 'contact' ? 'bg-primary text-white' : 'bg-canvas text-text-muted'}`}>{t('contactsGrid.columns.name')}</button>
            </div>
            {createLinkKind === 'company' ? (
              <select autoFocus data-testid="invoice-create-company" value={createCompanyId ?? ''} onChange={(e) => setCreateCompanyId(e.target.value || null)}
                className="h-10 px-3 rounded-lg border border-border-neutral text-sm">
                <option value="">{t('invoicesGrid.selectAccount')}</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            ) : (
              <select autoFocus data-testid="invoice-create-contact" value={createContactId ?? ''} onChange={(e) => setCreateContactId(e.target.value || null)}
                className="h-10 px-3 rounded-lg border border-border-neutral text-sm">
                <option value="">{t('invoicesGrid.selectContact')}</option>
                {contacts.map((c) => <option key={c.id} value={c.id}>{c.name ?? '—'}</option>)}
              </select>
            )}
            <input type="number" data-testid="invoice-create-amount" value={createAmount} onChange={(e) => setCreateAmount(e.target.value)}
              placeholder={t('invoicesGrid.columns.amount')} className="h-10 px-3 rounded-lg border border-border-neutral text-sm w-fit" />
            <label className="text-xs text-text-muted flex flex-col gap-1">
              {t('invoicesGrid.columns.dueDate')}
              <input data-testid="invoice-create-due" type="date" value={createDue} onChange={(e) => setCreateDue(e.target.value)}
                className="h-10 px-3 rounded-lg border border-border-neutral text-sm w-fit" />
            </label>
            {createError && <div className="text-xs text-opt-rose" data-testid="invoice-create-error">{t('writeStates.createFailed')}</div>}
            <button type="submit" disabled={!(createLinkKind === 'company' ? createCompanyId : createContactId) || !createAmount.trim()} data-testid="invoice-create-submit"
              className="h-10 px-4 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50 self-start">
              {t('invoicesGrid.create')}
            </button>
          </form>
        )}

        {pendingCreatedId && (
          <div className="p-6 text-center text-sm text-text-muted flex flex-col items-center gap-3" data-testid="invoice-create-unconfirmed">
            {t('writeStates.confirmFailed')}
            <button type="button" data-testid="invoice-create-unconfirmed-retry" onClick={() => void confirmCreated(pendingCreatedId)}
              className="min-h-[44px] px-4 rounded-lg border border-border-neutral text-sm font-semibold text-text-strong hover:bg-canvas">
              {t('writeStates.recheck')}
            </button>
          </div>
        )}

        {invoiceId !== 'new' && loadState === 'loading' && !invoice && <div className="p-6 text-center text-sm text-text-muted">…</div>}
        {loadState === 'error' && !invoice && <div className="p-6 text-center text-sm text-text-muted" data-testid="invoice-detail-error">{t('customers.detail.loadError')}</div>}

        {invoice && (
          <div data-testid="invoice-detail-content" aria-busy={isLoading}>
            {isLoading && (
              <div data-testid="invoice-detail-revalidating" className="text-xs text-text-muted text-center py-1" aria-live="polite">{t('customers.detail.loading')}</div>
            )}
            {/* identity — DE-FISCALISED (PM ruling, 2026-09-10). The record is named by its PAYER,
                not by the sequence. `number` is auto-generated, sequential per account and uniquely
                constrained — the exact artifact tax authorities regulate — while this product has no
                tax id and no tax fields. Presenting that sequence as the document's identity is what
                made a payment record read as a fiscal invoice. It survives as an internal reference
                (still unique, still useful for support), rendered muted and labelled Reference. */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <h2 data-testid="invoice-title" className="text-lg font-bold text-text-strong truncate">
                {invoice.company?.name ?? invoice.lead?.name ?? '—'}
              </h2>
              <div className="text-[11px] text-text-muted mt-0.5">
                {t('invoicesGrid.columns.number')}: <span data-testid="invoice-number" dir="ltr">{invoice.number || '—'}</span>
              </div>
              {/* B2/D-I2 — the amount of a sent/paid/void invoice is SETTLED: off-draft it renders
                  as plain text with NO click target. Absent, not disabled — a greyed field invites
                  hunting for the permission that unlocks it; absent says "this is settled", which
                  is the truth. The service refuses the write regardless (the UI is the mirror,
                  never the guard). `status === 'draft'` (not `!== 'marked_sent'`) so the empty-shell
                  revalidation state ('') offers no financial edit either. */}
              {editingField === 'amount' && invoice.status === 'draft' ? (
                <input autoFocus type="number" data-testid="invoice-amount-input" value={fieldDraft} onChange={(e) => setFieldDraft(e.target.value)}
                  onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingField(null); }}
                  className="mt-1 h-9 px-2 rounded-lg border border-primary text-sm w-fit" />
              ) : (
                <div data-testid="invoice-amount" className={`mt-1 text-sm text-text-strong ${invoice.status === 'draft' ? 'cursor-text' : ''}`}
                  onClick={invoice.status === 'draft' ? () => { if (!isLoading) startEdit('amount', String(invoice.amount)); } : undefined} dir="ltr">
                  {invoice.number ? invoice.amount.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
                </div>
              )}
              {/* D-I3 (DN-3 visibility shape) — two clocks on money: the stored total and the line
                  sum must never disagree INVISIBLY. The sum renders beside the amount whenever
                  lines exist; a mismatch is flagged with the word, never colour alone. */}
              {invoice.lineItems.length > 0 && (
                <div data-testid="invoice-line-sum" className="text-xs text-text-muted" dir="ltr">
                  {t('invoicesGrid.lineSum', { sum: invoice.lineSum.toLocaleString(undefined, { maximumFractionDigits: 0 }) })}
                </div>
              )}
              {invoice.lineItems.length > 0 && invoice.lineSum !== invoice.amount && (
                <div data-testid="invoice-line-sum-mismatch" className="text-xs font-semibold text-warning">
                  {t('invoicesGrid.lineSumMismatch')}
                </div>
              )}
            </div>

            {/* state — status (read-only pill) + due (inline edit) */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4 flex flex-col gap-3">
              {/* D-I1 — status is ACTION-driven (Send/Mark paid/Void); there is no picker and must
                  never be one, so the shape is SystemChip: square, no chevron, not a button. The old
                  "read-only AttributePill" announced "Edit status" and swallowed taps. */}
              <SystemChip label={invoice.status ? t(`invoicesGrid.status.${invoice.status}`) : '—'} title={t('invoicesGrid.columns.status')} testId="invoice-status-chip" />
              {editingField === 'dueDate' ? (
                <input autoFocus type="date" data-testid="invoice-due-input" value={fieldDraft} onChange={(e) => setFieldDraft(e.target.value)}
                  onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingField(null); }}
                  className="h-9 px-2 rounded-lg border border-primary text-sm w-fit" />
              ) : (
                <div className="text-sm cursor-text" onClick={() => { if (!isLoading) startEdit('dueDate', invoice.dueDate ? invoice.dueDate.slice(0, 10) : ''); }}>
                  {t('invoicesGrid.columns.dueDate')}: <DueDate dueDate={invoice.dueDate} status={invoice.status} />
                </div>
              )}

              {/* actions — Send/Mark paid are the neutral pair; Void is terminal, separated, rose */}
              <div className="flex items-center gap-2 flex-wrap">
                {invoice.status === 'draft' && <ActionChip testId="invoice-send" label={t('invoicesGrid.send')} onAct={() => setConfirmingSend(true)} disabled={isLoading} />}
                {(invoice.status === 'draft' || invoice.status === 'marked_sent') && <ActionChip testId="invoice-mark-paid" label={t('invoicesGrid.markPaid')} onAct={markPaid} disabled={isLoading} />}
              </div>
              {invoice.status !== 'paid' && invoice.status !== 'void' && invoice.number && (
                <div className="pt-1 border-t border-border-neutral">
                  <ActionChip testId="invoice-void" label={t('invoicesGrid.void')} onAct={() => setConfirmingVoid(true)} disabled={isLoading} title={t('invoicesGrid.void')} />
                </div>
              )}
            </div>

            {/* related — account, deal */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-1">{t('accountsGrid.columns.name')}</div>
              {/* B3: the payer is a company XOR a contact — render whichever root this invoice bills. */}
              <div data-testid="invoice-company" className="text-sm text-text-strong">{invoice.company?.name ?? invoice.lead?.name ?? '—'}</div>
            </div>

            {/* line items */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('invoicesGrid.lineItems')}</div>
              {invoice.lineItems.length > 0 ? (
                <div className="space-y-1.5" data-testid="invoice-line-items-list">
                  {invoice.lineItems.map((li) => (
                    <div key={li.id} className="flex items-center justify-between text-sm gap-2">
                      <span className="truncate text-text-strong">{li.description}</span>
                      <span className="text-xs text-text-muted shrink-0" dir="ltr">{li.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                      {/* D-LI1 — a destructive control on a phone gets the pill family's transparent
                          inset wrapper: 44px hit area, the visual glyph unchanged.
                          B2/D-LI3 — ABSENT off-draft, not disabled: the composition of a sent
                          invoice is what the customer received; there is no × to hunt for. */}
                      {invoice.status === 'draft' && (
                        <button type="button" aria-label={t('invoicesGrid.removeLineItem')} data-testid="invoice-line-item-remove"
                          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] p-0 bg-transparent shrink-0 text-text-muted hover:text-opt-rose" disabled={isLoading}
                          onClick={() => void removeLineItem(li.id)}>&times;</button>
                      )}
                    </div>
                  ))}
                </div>
              ) : <span className="text-text-muted italic text-sm">{t('invoicesGrid.noLineItems')}</span>}
              {/* B2/D-I2: the add affordance exists only on a draft — absent from the DOM after
                  send, same rule as the per-row ×. */}
              {isAddingLine && invoice.status === 'draft' ? (
                <form onSubmit={addLineItem} className="flex items-center gap-2 mt-2">
                  <input autoFocus data-testid="invoice-line-item-desc" value={lineDescDraft} onChange={(e) => setLineDescDraft(e.target.value)}
                    placeholder={t('invoicesGrid.lineItemDescPlaceholder')} className="h-9 px-2 rounded-lg border border-border-neutral text-sm flex-1" dir="auto" />
                  <input type="number" data-testid="invoice-line-item-amount" value={lineAmountDraft} onChange={(e) => setLineAmountDraft(e.target.value)}
                    placeholder={t('invoicesGrid.columns.amount')} className="h-9 px-2 rounded-lg border border-border-neutral text-sm w-20" />
                  {lineError && <div className="text-xs text-opt-rose" data-testid="invoice-line-item-error">{t('writeStates.saveFailed')}</div>}
                  <button type="submit" data-testid="invoice-line-item-add-submit" className="h-9 px-3 rounded-lg bg-primary text-white text-sm font-semibold">{t('pipelineGrid.confirm')}</button>
                </form>
              ) : invoice.status === 'draft' ? (
                <button type="button" data-testid="invoice-line-item-add-open" onClick={() => setIsAddingLine(true)} disabled={isLoading}
                  className="mt-2 text-sm font-semibold text-primary disabled:opacity-50">+ {t('invoicesGrid.addLineItem')}</button>
              ) : null}
            </div>

            {/* activity */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('customers.detail.recordHistory')}</div>
              {invoice.recentActivity.length > 0 ? (
                <div className="space-y-2" data-testid="invoice-timeline-list">
                  {invoice.recentActivity.map((a) => <div key={a.id} className="text-sm text-text-strong">{a.summary}</div>)}
                </div>
              ) : <span className="text-text-muted italic text-sm">{t('customers.detail.noChangesYet')}</span>}
            </div>
          </div>
        )}
      </div>

      {confirmingVoid && invoice && (
        <ConfirmDialog destructive
          message={t('invoicesGrid.voidConfirm', {
            account: invoice.company?.name ?? invoice.lead?.name ?? '—',
            amount: invoice.amount.toLocaleString(undefined, { maximumFractionDigits: 0 }),
          })}
          confirmLabel={t('invoicesGrid.void')}
          onConfirm={() => void voidInvoice()}
          onClose={() => setConfirmingVoid(false)} />
      )}
      {/* B2/D-I4 — not `destructive` (this is the product working, not data loss). Confirm-first
          survives, but NOT for the reason first given: `sendInvoice` transmits nothing, so there is
          no email to be un-sent. What is irreversible is the LINE-ITEM FREEZE (P16/D-I2), and that
          is what the copy discloses. The dialog also states plainly that nothing is emailed. */}
      {confirmingSend && invoice && (
        <ConfirmDialog
          message={t('invoicesGrid.sendConfirm', {
            account: invoice.company?.name ?? invoice.lead?.name ?? '—',
            amount: invoice.amount.toLocaleString(undefined, { maximumFractionDigits: 0 }),
          })}
          confirmLabel={t('invoicesGrid.send')}
          onConfirm={() => void send()}
          onClose={() => setConfirmingSend(false)} />
      )}
      {feedback.node}
    </div>
  );
};
